import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth, useBasic } from 'contexts';
import {
  getLotteries,
  getPlayerTickets,
  getLotteryPrizes,
  getRewardsConfig,
  getLotteryResults,
} from 'services';
import { Lottery, LotteryTicket, VirtualGood, RewardsConfig } from 'types';

import { HiOutlineTicket } from 'react-icons/hi';
import { IoTrophyOutline } from 'react-icons/io5';
import { BsCalendar3 } from 'react-icons/bs';
import { FiArrowRight } from 'react-icons/fi';

import './style.scss';

type TicketTab = 'all' | 'active';


function formatDate(ts: number): string {
  const d = new Date(ts);
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];
  return `${d.getDate()} de ${months[d.getMonth()]}`;
}

function formatDateShort(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function formatWeekday(ts: number): string {
  const d = new Date(ts);
  const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const hours = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}h`;
  return `${days[d.getDay()]}, às ${hours}`;
}

function getCountdown(drawDate: number): { days: string; hours: string; minutes: string } {
  const now = Date.now();
  const diff = Math.max(0, drawDate - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
  };
}

export default function TicketsPage() {
  const { user } = useAuth();
  const { setShowHeader, setShowFooter } = useBasic();

  const [loading, setLoading] = useState(true);
  const [rewardsConfig, setRewardsConfig] = useState<RewardsConfig | null>(null);
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [tickets, setTickets] = useState<LotteryTicket[]>([]);
  const [prizes, setPrizes] = useState<VirtualGood[]>([]);
  const [tab, setTab] = useState<TicketTab>('all');
  const [countdown, setCountdown] = useState({ days: '00', hours: '00', minutes: '00' });
  const [lotteryResults, setLotteryResults] = useState<Record<string, any>>({});

  useEffect(() => {
    setShowHeader(true);
    setShowFooter(false);
  }, [setShowHeader, setShowFooter]);


  useEffect(() => {
    async function loadData() {
      if (!user?._id) return;

      setLoading(true);
      try {
        const [config, lotteriesData, ticketsData, prizesData] = await Promise.all([
          getRewardsConfig(),
          getLotteries(),
          getPlayerTickets(user._id),
          getLotteryPrizes(),
        ]);

        setRewardsConfig(config);
        setLotteries(lotteriesData);
        setTickets(ticketsData);
        setPrizes(prizesData);

        // Buscar resultados dos sorteios realizados
        const resultsMap: Record<string, any> = {};
        for (const lottery of lotteriesData) {
          if (lottery.executedDate) {
            const results = await getLotteryResults(lottery._id, user._id);
            resultsMap[lottery._id] = results.length > 0 ? results[0] : null;
          }
        }
        setLotteryResults(resultsMap);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [user?._id]);


  const lotteryMap = useMemo(() => {
    const map: Record<string, Lottery> = {};
    lotteries.forEach(l => { map[l._id] = l; });
    return map;
  }, [lotteries]);


  const nextDraw = useMemo(() => {
    const now = Date.now();
    const upcoming = lotteries
      .filter(l => l.drawDate > now)
      .sort((a, b) => a.drawDate - b.drawDate);
    return upcoming[0] ?? null;
  }, [lotteries]);

  useEffect(() => {
    if (!nextDraw) return;
    const update = () => setCountdown(getCountdown(nextDraw.drawDate));
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [nextDraw]);

  const silverLotteryId = rewardsConfig?.tickets?.lottery70 ?? '';
  const goldLotteryId = rewardsConfig?.tickets?.lottery100 ?? '';

  const silverCount = useMemo(() => tickets.filter(t => t.lottery === silverLotteryId).length, [tickets, silverLotteryId]);
  const goldCount = useMemo(() => tickets.filter(t => t.lottery === goldLotteryId).length, [tickets, goldLotteryId]);

  const displayTickets = useMemo(() => {
    return tickets
      .map(t => {
        const lottery = lotteryMap[t.lottery];
        const isGold = t.lottery === goldLotteryId;
        return {
          _id: t._id,
          code: t.code ?? t._id,
          lotteryId: t.lottery,
          lotteryTitle: lottery?.title ?? 'Sorteio',
          drawDate: lottery?.drawDate ?? 0,
          type: isGold ? ('gold' as const) : ('silver' as const),
          created: t.created ?? 0,
          isActive: lottery ? lottery.drawDate > Date.now() : false,
        };
      })
      .sort((a, b) => b.created - a.created);
  }, [tickets, lotteryMap, goldLotteryId]);

  const filteredTickets = useMemo(() => {
    if (tab === 'active') return displayTickets.filter(t => t.isActive);
    return displayTickets;
  }, [displayTickets, tab]);

  const lotteryPrizes = useMemo(() => {
    const groups: { lottery: Lottery; items: VirtualGood[] }[] = [];

    const goldLottery = lotteryMap[goldLotteryId];
    if (goldLottery) {
      const goldItems = goldLottery.rewards
        .filter(r => r.item)
        .map(r => prizes.find(p => p._id === r.item))
        .filter(Boolean) as VirtualGood[];
      groups.push({ lottery: goldLottery, items: goldItems });
    }

    const silverLottery = lotteryMap[silverLotteryId];
    if (silverLottery) {
      const silverItems = silverLottery.rewards
        .filter(r => r.item)
        .map(r => prizes.find(p => p._id === r.item))
        .filter(Boolean) as VirtualGood[];
      groups.push({ lottery: silverLottery, items: silverItems });
    }

    return groups;
  }, [lotteryMap, goldLotteryId, silverLotteryId, prizes]);

  if (loading) {
    return (
      <div className="tickets-page flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-orange border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="tickets-page p-6 lg:p-10 max-w-[1400px] mx-auto">
      <h1 className="text-2xl font-bold text-[#181b1a] mb-8">Meus Prêmios e Bilhetes</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">

        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Saldo de Bilhetes</span>
          <div className="flex items-center gap-8 mt-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                <HiOutlineTicket className="text-gray-500 text-xl" />
              </span>
              <div>
                <span className="text-2xl font-bold text-[#181b1a]">{String(silverCount).padStart(2, '0')}</span>
                <span className="block text-xs text-gray-400">Prata</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <HiOutlineTicket className="text-amber-500 text-xl" />
              </span>
              <div>
                <span className="text-2xl font-bold text-[#181b1a]">{String(goldCount).padStart(2, '0')}</span>
                <span className="block text-xs text-gray-400">Ouro</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange rounded-2xl p-6 text-white">
          <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">Próximo Sorteio</span>
          {nextDraw ? (
            <div className="mt-3">
              <h3 className="text-xl font-bold">{formatDate(nextDraw.drawDate)}</h3>
              <p className="text-sm text-white/80 mt-1">{formatWeekday(nextDraw.drawDate)}</p>
            </div>
          ) : (
            <p className="text-sm text-white/80 mt-3">Nenhum sorteio agendado</p>
          )}
        </div>


        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <h3 className="text-base font-bold text-[#181b1a]">Como ganhar mais?</h3>
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">
            Avance nos cursos! Ao atingir 70% de progresso, você ganha um bilhete prateado. Ao concluir 100%, você recebe um bilhete dourado.
          </p>
        </div>
      </div>


      <div className="flex flex-col lg:flex-row gap-8">
 
        <div className="flex-1">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-[#181b1a]">Meus Bilhetes Individuais</h2>
            <div className="flex gap-3">
              <button
                onClick={() => setTab('all')}
                className={`text-sm font-medium px-1 pb-1 border-b-2 transition-colors ${
                  tab === 'all' ? 'border-orange text-orange' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setTab('active')}
                className={`text-sm font-medium px-1 pb-1 border-b-2 transition-colors ${
                  tab === 'active' ? 'border-orange text-orange' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                Ativos
              </button>
            </div>
          </div>

          {filteredTickets.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
              <HiOutlineTicket className="text-4xl text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400">Nenhum bilhete encontrado</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredTickets.map(ticket => {
                const ticketLottery = lotteryMap[ticket.lotteryId];
                
                const lotteryWasDrawn = ticketLottery?.executedDate ? true : false;
                
                const drawResult = lotteryWasDrawn ? lotteryResults[ticket.lotteryId] : null;
                
                const thisTicketWon = drawResult && drawResult.lottery_ticket === ticket._id;
                
                const lotteryPrize = ticketLottery?.rewards && ticketLottery.rewards.length > 0
                  ? prizes.find(p => p._id === ticketLottery.rewards[0].item)
                  : null;

                const drawDate = ticketLottery?.executedDate
                  ? new Date(ticketLottery.executedDate)
                  : ticketLottery?.drawDate
                  ? new Date(ticketLottery.drawDate)
                  : null;
                const formattedDrawDate = drawDate
                  ? `${String(drawDate.getDate()).padStart(2, '0')}/${String(drawDate.getMonth() + 1).padStart(2, '0')}/${drawDate.getFullYear()}`
                  : '';

                return (
                  <div
                    key={ticket._id}
                    className={`ticket-card relative rounded-2xl p-5 overflow-hidden border ${
                      lotteryWasDrawn && thisTicketWon
                        ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                        : ticket.type === 'gold'
                        ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200'
                        : 'bg-white border-gray-100'
                    }`}
                  >
                    <div className="ticket-notch left" />
                    <div className="ticket-notch right" />

                    {thisTicketWon ? (
                      <>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-green-700">
                          Bilhete {ticket.type === 'gold' ? 'Dourado' : 'Prateado'} Premiado
                        </span>

                        <div className="flex items-center justify-between mt-3 mb-3">
                          <div className="flex-1">
                            <h4 className="text-base font-bold text-[#181b1a] font-mono">
                              #{ticket.code.toUpperCase()}
                            </h4>
                          </div>
                          {lotteryPrize?.image?.small?.url ? (
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                              <img src={lotteryPrize.image.small.url} alt={lotteryPrize.name} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <span className={`w-8 h-8 rounded-lg ${ticket.type === 'gold' ? 'bg-amber-400' : 'bg-gray-300'} flex items-center justify-center flex-shrink-0`}>
                              <IoTrophyOutline className="text-white text-lg" />
                            </span>
                          )}
                        </div>

                        <p className="text-xs font-semibold text-green-700 leading-relaxed">
                          Parabéns! Você ganhou:
                        </p>
                        <p className="text-sm font-bold text-green-800 mt-1">
                          {lotteryPrize?.name || 'Prêmio'}
                        </p>

                        <p className="text-xs text-green-600 mt-2">
                          Sorteado em {formattedDrawDate}
                        </p>
                      </>
                    ) : (
                      <>
                        <span
                          className={`text-[10px] font-bold uppercase tracking-widest ${
                            ticket.type === 'gold' ? 'text-amber-600' : 'text-gray-400'
                          }`}
                        >
                          Bilhete {ticket.type === 'gold' ? 'Dourado' : 'Prateado'}
                        </span>

                        <div className="flex items-center justify-between mt-2">
                          <h4 className="text-base font-bold text-[#181b1a] font-mono">
                            #{ticket.code.toUpperCase()}
                          </h4>
                          {ticket.type === 'gold' && (
                            <span className="w-8 h-8 rounded-lg bg-amber-400 flex items-center justify-center">
                              <HiOutlineTicket className="text-white text-lg" />
                            </span>
                          )}
                          {ticket.type === 'silver' && (
                            <span className="w-8 h-8 rounded-lg bg-gray-300 flex items-center justify-center">
                              <HiOutlineTicket className="text-white text-lg" />
                            </span>
                          )}
                        </div>

                        {lotteryWasDrawn ? (
                          <>
                            <p className="text-xs font-semibold text-gray-500 leading-relaxed mt-2">
                              <p>
                                Não foi desta vez
                              </p>
                              <p>
                                Mas não desista!
                              </p>
                              <p>
                                Assista mais cursos para ganhar novos bilhetes.
                              </p>
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Sorteado em {formattedDrawDate}
                            </p>
                          </>
                        ) : (
                          <p className={`text-xs mt-2 ${ticket.type === 'gold' ? 'text-amber-600' : 'text-gray-400'}`}>
                            Sorteio em {formattedDrawDate}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>


        <div className="w-full lg:w-[320px] flex-shrink-0">
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-base font-bold text-[#181b1a] mb-5 flex items-center gap-2">
              <IoTrophyOutline className="text-orange" />
            </h3>

            {lotteryPrizes.map(group => (
              <div key={group.lottery._id} className="mb-6 last:mb-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-5 h-5 rounded flex items-center justify-center text-xs ${
                    group.lottery._id === goldLotteryId
                      ? 'bg-amber-400 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    <HiOutlineTicket className="text-sm" />
                  </span>
                  <span className="text-sm font-bold text-[#181b1a] uppercase">
                    Sorteio {group.lottery._id === goldLotteryId ? 'Ouro' : 'Prata'}
                  </span>
                  <span>
                    {formatDateShort(group.lottery.drawDate)}
                  </span>
                </div>

                {group.items.length > 0 ? (
                  group.items.map(prize => (
                    <div
                      key={prize._id}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors mb-2"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {prize.image?.small?.url ? (
                          <img src={prize.image.small.url} alt={prize.name} className="w-full h-full object-cover" />
                        ) : (
                          <IoTrophyOutline className="text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-[#181b1a] truncate">
                          {prize.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {prize.amount === -1 ? 'Ilimitado' : `${prize.amount} unidades disponíveis`}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 pl-7">Prêmios em breve</p>
                )}
              </div>
            ))}

            {nextDraw && (
              <div className="mt-6 pt-5 border-t border-gray-100 text-center">
                <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">
                  Faltam para o sorteio:
                </span>
                <div className="flex items-center justify-center gap-3 mt-3">
                  <div className="text-center">
                    <span className="text-2xl font-bold text-orange">{countdown.days}</span>
                    <span className="block text-[10px] text-gray-400 uppercase">Dias</span>
                  </div>
                  <span className="text-xl font-bold text-orange">:</span>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-orange">{countdown.hours}</span>
                    <span className="block text-[10px] text-gray-400 uppercase">Horas</span>
                  </div>
                  <span className="text-xl font-bold text-orange">:</span>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-orange">{countdown.minutes}</span>
                    <span className="block text-[10px] text-gray-400 uppercase">Min</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
